import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Chip, IconButton, Alert, Link, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { getSourcesAndExamples } from '../utils/sources';

interface NodeDetails {
  id?: string;
  type: string;
  properties: Record<string, any>;
}

interface LinkedElementInfo { elementId: string; name?: string; roles?: string[] }
interface CuratedNodeDetailsProps {
  node: NodeDetails;
  linkedElementsByDomain?: Record<string, LinkedElementInfo[]>;
  headerActions?: React.ReactNode;
}

const CuratedNodeDetails: React.FC<CuratedNodeDetailsProps> = ({ node, linkedElementsByDomain, headerActions }) => {
  const [sectionExpanded, setSectionExpanded] = useState<Record<number, boolean>>({});
  const [companyUI, setCompanyUI] = useState<Record<number, { addrExpanded?: boolean; linkedExpanded?: boolean; reasoningExpanded?: boolean }>>({});

  const normalizeDomainKey = (value: any): string => {
    let s = String(value || '').trim().toLowerCase();
    if (!s) return '';
    s = s.replace(/^https?:\/\//, '');
    s = s.replace(/^www\./, '');
    s = s.replace(/\/+$/, '');
    return s;
  };

  const synonymsText = useMemo(() => {
    const syn = node?.properties?.synonyms;
    if (Array.isArray(syn)) {
      // Filter, trim, and deduplicate synonyms
      const cleaned = syn.filter(Boolean).map(String).map(s => s.trim()).filter(s => s.length > 0);
      const unique = Array.from(new Set(cleaned));
      return unique.join('; ');
    }
    return typeof syn === 'string' ? syn : '';
  }, [node]);

  const toSentence = (value: any): string => {
    if (Array.isArray(value)) {
      const flat = value.flat().filter(Boolean).map(String).map(s => s.trim()).filter(s => s.length > 0);
      return flat.join('; ');
    }
    return typeof value === 'string' ? value : (value != null ? String(value) : '');
  };

  const toArray = (value: any): string[] => {
    if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v).trim()).filter((v) => v.length > 0);
    if (typeof value === 'string') return value.split(/\s*;\s*|\s*\n\s*|\s*,\s*/).map((v) => v.trim()).filter((v) => v.length > 0);
    return [];
  };

  const isUrl = (s: string): boolean => {
    if (typeof s !== 'string') return false;
    try { const u = new URL(s); return !!u.protocol && !!u.host; } catch { return s.startsWith('http://') || s.startsWith('https://'); }
  };

  const sections = useMemo(() => {
    const p = node?.properties || {};
    const t = node?.type;
    if (t === 'System' || t === 'Material') {
      return [
        {
          title: 'Characteristics',
          rows: [
            { label: 'Function', value: toSentence(p.function) },
            { label: 'Mechanism', value: toSentence(p.mechanism) },
            { label: 'Specifications', value: toSentence(p.specifications) },
            { label: 'Core Components', value: toSentence(p.core_components) },
            { label: 'Source References', values: toArray(p.source_references) },
          ]
        },
        {
          title: 'Type Distinction',
          rows: [
            { label: 'Distinguishing Features of its types', value: toSentence(p.type_distinction) }
          ]
        }
      ];
    }
    if (t === 'TechnologyType' || t === 'NoProductTechnologyType') {
      const { sourcesVal, sourcesLabel, exampleProductsLabel, exampleProductsValues } = getSourcesAndExamples(t, p);
      const combinedPrice = [toSentence(p.price_range), toSentence(p.price_range_unit)].filter(s => s && s.trim().length > 0).join(' ');
      const codeList = toArray(p.hs_code);
      const codeDescList = toArray(p.hs_code_description);
      const codeWithDesc = codeList.length > 0 ? codeList.map((c, i) => (codeDescList[i] ? `${c} ${codeDescList[i]}` : c)) : [];
      return [
        {
          title: 'Characteristics',
          rows: [
            { label: 'Function', value: toSentence(p.function) },
            { label: 'Mechanism', value: toSentence(p.mechanism) },
            { label: 'Specifications', value: toSentence(p.specifications) },
            { label: 'Distinguishing Features', value: toSentence(p.distinguishing_features) },
            { label: 'Core Components', value: toSentence(p.core_components) },
            { label: sourcesLabel, values: toArray(sourcesVal) },
          ]
        },
        {
          title: 'Prevalence',
          rows: [
            { label: 'Description', value: toSentence(p.prevalence) },
            { label: 'Rating', value: toSentence(p.prevalence_rating) },
            { label: 'Prevalence Sources', values: toArray(p.prevalence_sources) },
          ]
        },
        {
          title: 'HS Codes',
          rows: [
            { label: 'HS Code', values: codeWithDesc },
            { label: 'HS Code Explanation', values: toArray(p.hs_code_explanation) },
          ]
        },
        {
          title: 'Examples',
          rows: [
            { label: 'Price', value: combinedPrice },
            exampleProductsLabel ? { label: exampleProductsLabel, values: toArray(exampleProductsValues) } : null,
          ].filter(Boolean)
        }
      ];
    }
    return [];
  }, [node]);

  const companiesSection = useMemo(() => {
    const companies = Array.isArray(node?.properties?.companies) ? node.properties.companies : [];
    if (!companies || companies.length === 0) return null;
    // Render a single grid that spans both columns; each company uses a two-column layout
    const content = (
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
        {companies.map((c: any, idx: number) => {
          const companyName = c?.company_name || 'Company';
          const companyDomains: string[] = (() => {
            const arr = Array.isArray(c?.company_domains)
              ? c.company_domains.map((d: any) => String(d || '').trim()).filter((d: string) => d.length > 0)
              : [];
            if (arr.length > 0) return Array.from(new Set(arr)) as string[];
            const single = String(c?.company_domain || '').trim();
            return single ? [single] : [];
          })();
          const companyDomain: string | undefined = companyDomains[0];
          const registerSpecificCompanyId = String(c?.register_specific_company_id || '').trim();
          const rolesArr = Array.isArray(c?.company_roles) ? c.company_roles : (typeof c?.company_roles === 'string' ? [c.company_roles] : []);
          const addresses = Array.isArray(c?.addresses) ? c.addresses : [];
          const ui = companyUI[idx] || {};

          // Deduplicate and format addresses
          const byAddress: Map<string, {
            address: string; country?: string; lat?: number; lng?: number; emails: Set<string>;
          }> = new Map();
          addresses.forEach((a: any) => {
            const coordKey = (companyDomain && a?.latitude != null && a?.longitude != null)
              ? `${companyDomain}|${a.longitude}|${a.latitude}`
              : undefined;
            const displayText = String((a?.formatted_address || a?.address || '')).trim();
            const dedupeKey = coordKey || (displayText ? displayText : undefined);
            if (!dedupeKey) return; // do not show an address line if no readable address is available
            const hasCoords = a?.latitude != null && a?.longitude != null;
            const existing = byAddress.get(dedupeKey);
            if (!existing) {
              byAddress.set(dedupeKey, { address: displayText, country: a?.country, lat: hasCoords ? a.latitude : undefined, lng: hasCoords ? a.longitude : undefined, emails: new Set(a?.email ? [String(a.email)] : []) });
            } else {
              if (!existing.country && a?.country) existing.country = a.country;
              if (hasCoords && (existing.lat == null || existing.lng == null)) { existing.lat = a.latitude; existing.lng = a.longitude; }
              if (a?.email) existing.emails.add(String(a.email));
            }
          });
          const addrLines = Array.from(byAddress.values()).sort((x, y) => {
            const xc = x.lat != null && x.lng != null ? 1 : 0; const yc = y.lat != null && y.lng != null ? 1 : 0;
            if (yc !== xc) return yc - xc; return x.address.localeCompare(y.address);
          }).map(e => {
            const country = e.country ? ` (${e.country})` : '';
            const emails = e.emails.size > 0 ? ` — ${Array.from(e.emails).join(', ')}` : '';
            return `${e.address}${country}${emails}`;
          });
          const addrLimit = 1;
          const displayAddrLines = ui.addrExpanded ? addrLines : addrLines.slice(0, addrLimit);

          // Linked elements for this domain, excluding the current element
          const linkedRaw = (() => {
            if (!linkedElementsByDomain) return [];
            const candidates: string[] = [];
            if (registerSpecificCompanyId) candidates.push(`reg:${registerSpecificCompanyId.toLowerCase()}`);
            companyDomains.forEach((d) => {
              const norm = normalizeDomainKey(d);
              if (norm) candidates.push(`dom:${norm}`);
              // Backward-compat fallback for older callers keyed by raw domain.
              candidates.push(d);
            });
            const merged: any[] = [];
            const seen = new Set<string>();
            candidates.forEach((k) => {
              const rows = Array.isArray((linkedElementsByDomain as any)[k]) ? (linkedElementsByDomain as any)[k] : [];
              rows.forEach((r: any) => {
                const id = String(r?.elementId || '');
                const key = id ? `id:${id}` : JSON.stringify(r || {});
                if (seen.has(key)) return;
                seen.add(key);
                merged.push(r);
              });
            });
            return merged;
          })();
          const linkedList = linkedRaw.filter((le: any) => String(le?.elementId) !== String(node?.id));
          const linkedLimit = 1;
          const displayLinked = ui.linkedExpanded ? linkedList : linkedList.slice(0, linkedLimit);

          return (
            <Box key={`company-${idx}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, p: 1.5, border: '1px solid', borderColor: (t) => t.palette.divider, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.35)' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <span>{companyName}</span>
                  {rolesArr.length > 0 && (
                    <>
                      <span>—</span>
                      <span>{rolesArr.join(', ')}</span>
                    </>
                  )}
                </Typography>
                {companyDomains.length > 0 && (
                  <Box sx={{ mt: 0.35, display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      Domain{companyDomains.length > 1 ? 's' : ''}:
                    </Typography>
                    {companyDomains.map((d: string, di: number) => (
                      <React.Fragment key={`dom-${idx}-${di}`}>
                        <Link
                          href={(d.startsWith('http://') || d.startsWith('https://')) ? d : `https://${d}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {d}
                        </Link>
                        {di < companyDomains.length - 1 && (
                          <Typography component="span" variant="caption" color="text.secondary">,</Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
                {addrLines.length > 0 ? (
                  <Box sx={{ mt: 0.5, display: 'grid', gap: 0.25 }}>
                    {displayAddrLines.map((line, i) => (
                      <Typography key={`addr-${i}`} variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{line}</Typography>
                    ))}
                    {addrLines.length > addrLimit && (
                      <Box>
                        <Button size="small" variant="text" onClick={() => setCompanyUI(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), addrExpanded: !ui.addrExpanded } }))}>
                          {ui.addrExpanded ? 'Show fewer addresses' : `Show all addresses (${addrLines.length})`}
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No addresses</Typography>
                )}
                {companyDomain && linkedList.length > 0 && (
                  <Box sx={{ mt: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Other provided upstream elements:</Typography>
                    <Box sx={{ display: 'grid', gap: 0.25, mt: 0.25 }}>
                      {displayLinked.map((le: any, li: number) => (
                        <Typography key={`le-${li}`} variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                          {(le?.name || le?.elementId || '').toString()} {Array.isArray(le?.roles) && le.roles.length > 0 ? `— roles: ${le.roles.join(', ')}` : ''}
                        </Typography>
                      ))}
                      {linkedList.length > linkedLimit && (
                        <Box>
                          <Button size="small" variant="text" onClick={() => setCompanyUI(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), linkedExpanded: !ui.linkedExpanded } }))}>
                            {ui.linkedExpanded ? 'Show fewer elements' : `Show all elements (${linkedList.length})`}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Financial data section removed */}
              </Box>
              <Box>
                {c?.reasoning ? (
                  (() => {
                    const text = String(c.reasoning);
                    const byNewline = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
                    // If there's only one long paragraph, split by sentences to approximate "rows"
                    const bySentence = byNewline.length > 1 ? byNewline : text.split(/(?<=[.!?])\s+/).map(s => s.trim()).filter(s => s.length > 0);
                    const limit = 1;
                    const uiReason = companyUI[idx]?.reasoningExpanded;
                    const display = uiReason ? bySentence : bySentence.slice(0, limit);
                    const total = bySentence.length;
                    return (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Reasoning</Typography>
                        <Box sx={{ mt: 0.5, display: 'grid', gap: 0.25 }}>
                          {display.length > 0 ? display.map((ln, i) => (
                            <Typography key={`rs-${i}`} variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{ln}</Typography>
                          )) : (
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{text}</Typography>
                          )}
                          {total > limit && (
                            <Box>
                              <Button size="small" variant="text" onClick={() => setCompanyUI(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), reasoningExpanded: !uiReason } }))}>
                                {uiReason ? 'Show fewer paragraphs' : `Show all paragraphs (${total})`}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </>
                    );
                  })()
                ) : (
                  <Typography variant="body2" color="text.secondary">&nbsp;</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );

    return { title: 'Companies', rows: [{ label: '', values: [content] }], spanFull: true } as any;
  }, [node, companyUI, linkedElementsByDomain]);

  const reviewAlert = useMemo(() => {
    const p = node?.properties || {};
    const val = p.review_flag;
    const desc = p.review_flag_description;
    const isTrue = val === true || val === 1 || (typeof val === 'string' && val.toLowerCase() === 'true');
    return isTrue && desc && String(desc).trim().length > 0 ? String(desc) : '';
  }, [node]);

  const allSections = useMemo(() => {
    const base = sections || [];
    return companiesSection ? [...base, companiesSection] : base;
  }, [sections, companiesSection]);

  // Note: We keep per-company expand states independent of the section's expand state

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {node?.properties?.name || 'Node Details'}
          </Typography>
          {headerActions}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={node?.type || 'Unknown'} color="primary" size="small" variant="outlined" />
          {synonymsText ? (
            <Typography variant="body2" color="text.secondary">Synonyms: {synonymsText}</Typography>
          ) : null}
        </Box>
      </Box>

      {/* Review flag */}
      {reviewAlert ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{reviewAlert}</Alert>
      ) : null}

      {/* Sections */}
      {allSections.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {allSections.map((section: any, si) => {
            const isCompanies = section?.title === 'Companies';
            const expanded = isCompanies
              ? (sectionExpanded[si] ?? true) // default expanded, but user can collapse
              : !!sectionExpanded[si];
            return (
              <Box key={`sec-${si}`} sx={{ p: 0, gridColumn: section?.spanFull ? '1 / -1' : undefined }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
                  {section.title}
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={expanded ? {} : { maxHeight: '10.5em', overflow: 'hidden' }}>
                    {section.rows.map((row: any, idx: number) => {
                      if (!row) return null;
                      const val = row.value;
                      const vals = row.values;
                      if ((val == null || String(val).trim().length === 0) && (!Array.isArray(vals) || vals.length === 0)) return null;
                      return (
                        <Box key={`${section.title}-${row.label}-${idx}`} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{row.label}</Typography>
                          {Array.isArray(vals) && vals.length > 0 ? (
                            <Box sx={{ display: 'grid', gap: 0.25 }}>
                              {vals.map((v: string, i: number) => (
                                <Box key={`${section.title}-${row.label}-${idx}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isUrl(v) ? (
                                    <>
                                      <Typography component="a" href={v} target="_blank" rel="noopener noreferrer" variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{v}</Typography>
                                      <IconButton size="small" onClick={() => navigator.clipboard?.writeText(v)} aria-label="Copy link">
                                        <ContentCopyIcon fontSize="inherit" />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{v}</Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{val}</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {!expanded && (
                    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 24, background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(0,0,0,0.04))' }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <IconButton size="small" onClick={() => setSectionExpanded(prev => ({ ...prev, [si]: !expanded }))} aria-label={expanded ? 'Collapse' : 'Expand'} sx={{ mt: 0.5 }}>
                    {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">No details available for this node.</Typography>
      )}
    </Paper>
  );
};

export default CuratedNodeDetails;


